
/**
 * Feature to Menu Mapping Configuration
 * 
 * Maps feature keys to their associated menu items and routes.
 * Each feature can have different menu items for different roles.
 */

export const FEATURE_MENU_MAPPING = {
  // Dashboard
  dashboard: {
    menuItems: {
      schoolAdmin: ['dashboard'],
      teacher: ['dashboard'],
      student: []
    },
    routes: ['/dashboard/dashboard'],
    allRoles: true
  },

  // User Management
  students: {
    menuItems: {
      schoolAdmin: ['students'],
      teacher: ['students'],
      student: []
    },
    routes: ['/dashboard/students', '/dashboard/students/*'],
    roles: ['schoolAdmin', 'teacher']
  },

  teachers: {
    menuItems: {
      schoolAdmin: ['teachers'],
      teacher: ['teachers'],
      student: []
    },
    routes: ['/dashboard/teachers', '/dashboard/teachers/*'],
    roles: ['schoolAdmin', 'teacher']
  },

  staff: {
    menuItems: {
      schoolAdmin: ['staff'],
      teacher: ['staff'],
      student: []
    },
    routes: ['/dashboard/staff', '/dashboard/staff/*'],
    roles: ['schoolAdmin', 'teacher']
  },

  // Attendance Features
  clockInOutAttendance: {
    menuItems: {
      schoolAdmin: ['teacher-attendance', 'shift-assignment', 'staff-leave', 'staff-attendance'],
      teacher: ['my-attendance', 'student-leave'],
      student: []
    },
    routes: [
      '/dashboard/teacher-attendance',
      '/dashboard/shift-assignment',
      '/dashboard/staff-leave',
      '/dashboard/staff-attendance'
    ],
    allRoles: true
  },

  biometricMachineAttendance: {
    menuItems: {
      schoolAdmin: ['teacher-attendance'],
      teacher: [],
      student: []
    },
    routes: ['/dashboard/teacher-attendance'],
    allRoles: true
  },

  biometricOtgDevice: {
    menuItems: {
      schoolAdmin: ['teacher-attendance'],
      teacher: [],
      student: []
    },
    routes: ['/dashboard/teacher-attendance'],
    allRoles: true
  },

  webcamAttendance: {
    menuItems: {
      schoolAdmin: ['registration-faces'],
      teacher: [],
      student: []
    },
    routes: ['/dashboard/registration-faces'],
    roles: ['schoolAdmin']
  },

  manualAttendance: {
    menuItems: {
      schoolAdmin: ['teacher-attendance'],
      teacher: ['teacher-attendance'],
      student: []
    },
    routes: ['/dashboard/teacher-attendance'],
    roles: ['schoolAdmin', 'teacher']
  },

  // Bus Management
  busManagement: {
    menuItems: {
      schoolAdmin: ['bus-routes', 'live-tracking', 'drivers', 'fuel'],
      teacher: ['bus-tracking', 'bustracking-teacher'],
      student: ['bustracking']
    },
    routes: [
      '/dashboard/bus-routes',
      '/dashboard/bus-routes/*',
      '/dashboard/live-tracking',
      '/dashboard/live-tracking/*',
      '/dashboard/drivers',
      '/dashboard/fuel',
      '/dashboard/bus-tracking'
    ],
    allRoles: true
  },

  // Fee Management
  feeManagement: {
    menuItems: {
      schoolAdmin: ['fee-management'],
      teacher: ['fee-management'],
      student: ['fees']
    },
    routes: ['/dashboard/fee-management', '/dashboard/fee-management/*', '/dashboard/fees'],
    allRoles: true
  },

  lateFee: {
    menuItems: {
      schoolAdmin: ['fee-management'],
      teacher: ['fee-management'],
      student: []
    },
    routes: ['/dashboard/fee-management', '/dashboard/fee-management/*'],
    roles: ['schoolAdmin', 'teacher']
  },

  paymentMethods: {
    menuItems: {
      schoolAdmin: ['fee-management'],
      teacher: ['fee-management'],
      student: []
    },
    routes: ['/dashboard/fee-management', '/dashboard/fee-management/*'],
    roles: ['schoolAdmin', 'teacher']
  },

  razorpayRoute: {
    menuItems: {
      schoolAdmin: ['fee-management'],
      teacher: [],
      student: ['fees']
    },
    routes: ['/dashboard/fee-management', '/dashboard/fee-management/*', '/dashboard/fees'],
    allRoles: true
  },


  // Inventory
  inventory: {
    menuItems: {
      schoolAdmin: ['inventory', 'customer-ledger', 'inventory-analytics'],
      teacher: ['inventory'],
      student: []
    },
    routes: [
      '/dashboard/inventory',
      '/dashboard/inventory/*'
    ],
    roles: ['schoolAdmin', 'teacher']
  },

  customerLedger: {
    menuItems: {
      schoolAdmin: ['customer-ledger'],
      teacher: [],
      student: []
    },
    routes: [
      '/dashboard/customer-ledger',
      '/dashboard/customer-ledger/*'
    ],
    roles: ['schoolAdmin']
  },

  assetManagement: {
    menuItems: {
      schoolAdmin: ['assets'],
      teacher: ['assets'],
      student: []
    },
    routes: ['/dashboard/assets', '/dashboard/assets/*'],
    roles: ['schoolAdmin', 'teacher']
  },

  vendorManagement: {
    menuItems: {
      schoolAdmin: ['vendor'],
      teacher: ['vendor'],
      student: []
    },
    routes: ['/dashboard/vendor', '/dashboard/vendor/*'],
    roles: ['schoolAdmin', 'teacher']
  },


  // Holidays & Salary
  holidays: {
    menuItems: {
      schoolAdmin: ['holiday'],
      teacher: ['holiday'],
      student: []
    },
    routes: ['/dashboard/holiday'],
    roles: ['schoolAdmin', 'teacher']
  },

  salary: {
    menuItems: {
      schoolAdmin: ['payroll'],
      teacher: ['payroll', 'leave'],
      student: []
    },
    routes: ['/dashboard/payroll', '/dashboard/payroll/*'],
    roles: ['schoolAdmin', 'teacher']
  },

  // Announcements
  announcements: {
    menuItems: {
      schoolAdmin: ['announcements'],
      teacher: ['announcements'],
      student: ['announcements']
    },
    routes: ['/dashboard/announcements'],
    allRoles: true
  },

  alerts: {
    menuItems: {
      schoolAdmin: ['alert'],
      teacher: ['alert'],
      student: []
    },
    routes: ['/dashboard/alert'],
    roles: ['schoolAdmin', 'teacher']
  },

  // Settings Category Features
  classTransition: {
    menuItems: {
      schoolAdmin: ['class-transition'],
      teacher: ['class-transition'],
      student: []
    },
    routes: ['/dashboard/class-transition'],
    roles: ['schoolAdmin', 'teacher']
  },

  classroomMapping: {
    menuItems: {
      schoolAdmin: ['classroom-mapping'],
      teacher: ['classroom-mapping', 'class-students'],
      student: []
    },
    routes: ['/dashboard/classroom-mapping'],
    roles: ['schoolAdmin', 'teacher']
  },

  // Settings - Always show settings if any setting feature is enabled
  about: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: ['settings'],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin', 'teacher']
  },

  classSection: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: ['settings'],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin', 'teacher']
  },

  subjects: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: ['settings'],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin', 'teacher']
  },

  academicYears: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: ['settings'],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin', 'teacher']
  },

  permissions: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: [],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin']
  },

  departments: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: ['settings'],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin', 'teacher']
  },

  designations: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: ['settings'],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin', 'teacher']
  },

  workShifts: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: ['settings'],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin', 'teacher']
  },

  wings: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: ['settings'],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin', 'teacher']
  },

  // Feature Management - Shows the new feature management UI
  permissions: {
    menuItems: {
      schoolAdmin: ['settings', 'feature-management'],
      teacher: [],
      student: []
    },
    routes: ['/dashboard/settings', '/dashboard/feature-management'],
    roles: ['schoolAdmin']
  },

  receiptCustomization: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: [],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin']
  },

  emailManagement: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: [],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin']
  },

  faq: {
    menuItems: {
      schoolAdmin: ['faq'],
      teacher: ['faq'],
      student: []
    },
    routes: ['/dashboard/faq'],
    roles: ['schoolAdmin', 'teacher']
  },

  biometricIntegration: {
    menuItems: {
      schoolAdmin: ['biometric-integration'],
      teacher: [],
      student: []
    },
    routes: ['/dashboard/biometric-integration'],
    roles: ['schoolAdmin']
  },

  sectionSubjectMapping: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: ['settings'],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin', 'teacher']
  },

  studentSubjectEnrollment: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: ['settings'],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin', 'teacher']
  },

  paymentMethodsSettings: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: [],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin']
  },

  razorpaySettings: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: [],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin']
  },

  lateFeeSettings: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: [],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin']
  },

  attendanceSettings: {
    menuItems: {
      schoolAdmin: ['settings'],
      teacher: [],
      student: []
    },
    routes: ['/dashboard/settings'],
    roles: ['schoolAdmin']
  }
}

/**
 * Get menu items that should be shown for a specific role based on enabled features
 * @param {string} role - User role (schoolAdmin, teacher, student)
 * @param {Object} features - Feature flags object
 * @returns {Set<string>} Set of menu item IDs that should be shown
 */
export function getEnabledMenuItems(role, features) {
  const enabledMenuItems = new Set()

  // FAQ is always enabled for schoolAdmin and teacher regardless of feature flag
  if (role === 'schoolAdmin' || role === 'teacher') {
    enabledMenuItems.add('faq')
  }

  // Billing is an account-level concern, not a toggleable inventory feature — always
  // visible to schoolAdmin regardless of feature flags (mirrors FAQ above). BillingContent
  // itself further gates on tenantType (Industry-only) and shows a friendly message otherwise.
  if (role === 'schoolAdmin') {
    enabledMenuItems.add('billing')
  }

  // Profile is never feature-gated - every staff user can see their own profile.
  // staff-leave/staff-attendance stay feature-gated (clockInOutAttendance) and
  // are further gated by permission downstream in SchoolAdminDashboard2.
  enabledMenuItems.add('profile')

  // Iterate through all feature mappings
  Object.entries(FEATURE_MENU_MAPPING).forEach(([featureKey, mapping]) => {
    // Check if feature is enabled
    if (features[featureKey]?.enabled === true) {
      // Check if this role should see this feature
      if (mapping.allRoles || mapping.roles?.includes(role)) {
        // Add all menu items for this role
        const menuItems = mapping.menuItems[role] || []
        menuItems.forEach(item => enabledMenuItems.add(item))
      }
    }
  })

  return enabledMenuItems
}

/**
 * Check if a specific route should be accessible based on enabled features
 * @param {string} path - Route path to check
 * @param {string} role - User role
 * @param {Object} features - Feature flags object
 * @returns {boolean} True if route should be accessible
 */
export function isRouteEnabled(path, role, features) {
  // Check each feature mapping
  for (const [featureKey, mapping] of Object.entries(FEATURE_MENU_MAPPING)) {
    // Check if feature is enabled
    if (features[featureKey]?.enabled === true) {
      // Check if this role can access this feature
      if (mapping.allRoles || mapping.roles?.includes(role)) {
        // Check if path matches any of the routes
        const matchesRoute = mapping.routes.some(route => {
          if (route.endsWith('/*')) {
            const baseRoute = route.slice(0, -2)
            return path.startsWith(baseRoute)
          }
          return path === route
        })
        
        if (matchesRoute) {
          return true
        }
      }
    }
  }
  
  return false
}
